(() => {
  const root = () => (window.Shopify?.routes?.root || '/');
  let lastTrigger = null;
  let messageTimer = null;
  let activeMessageIndex = 0;
  let cartUpdateVersion = 0;
  let updateCompleteTimer = null;
  let toastTimer = null;
  let toastClearTimer = null;
  let cartItemsPreference = null;
  let recommendationsOpenTimer = null;

  const drawer = () => document.querySelector('pocket-cart');
  const panel = () => drawer()?.querySelector('.pocket-cart__panel');

  function setRecommendationsOpen(isOpen) {
    const accordion = drawer()?.querySelector('[data-recommendations-accordion]');
    if (!accordion) return;
    const toggle = accordion.querySelector('[data-recommendations-toggle]');
    const list = accordion.querySelector('[data-recommendations-list]');
    accordion.classList.toggle('is-open', isOpen);
    accordion.closest('[data-pocket-cart-recommendations]')?.classList.toggle('is-recommendations-open', isOpen);
    toggle?.setAttribute('aria-expanded', String(isOpen));
    if (list) list.hidden = !isOpen;
  }

  function scheduleRecommendationsOpen() {
    clearTimeout(recommendationsOpenTimer);
    const accordion = drawer()?.querySelector('[data-recommendations-accordion]');
    if (!accordion) return;
    setRecommendationsOpen(true);
  }

  function setUpdating(isUpdating) {
    const cart = drawer();
    if (!cart) return;
    const wasUpdating = cart.classList.contains('is-updating');
    clearTimeout(updateCompleteTimer);
    if (isUpdating) cart.classList.remove('is-update-complete');
    cart.classList.toggle('is-updating', isUpdating);
    cart.querySelector('.pocket-cart__panel')?.setAttribute('aria-busy', isUpdating ? 'true' : 'false');
    if (!isUpdating && wasUpdating) {
      cart.classList.add('is-update-complete');
      updateCompleteTimer = window.setTimeout(() => cart.classList.remove('is-update-complete'), 400);
    }
  }

  function beginCartUpdate() {
    cartUpdateVersion += 1;
    setUpdating(true);
    return cartUpdateVersion;
  }

  function showCartToast(message, type = 'success', duration) {
    const toast = drawer()?.querySelector('[data-cart-toast]');
    if (!toast) return;
    clearTimeout(toastTimer);
    clearTimeout(toastClearTimer);
    toast.textContent = message;
    toast.classList.toggle('is-error', type === 'error');
    toast.classList.add('is-visible');
    const displayDuration = duration ?? (type === 'error' ? 15000 : 2600);
    toastTimer = window.setTimeout(() => {
      toast.classList.remove('is-visible');
      // Clear the announced text once the toast is hidden so stale error/success
      // messages don't linger in the aria-live region for assistive tech.
      toastClearTimer = window.setTimeout(() => { toast.textContent = ''; }, 300);
    }, displayDuration);
  }

  async function createCartResponseError(response) {
    let responseDetail = '';
    try {
      const responseText = await response.text();
      try {
        const responseData = JSON.parse(responseText);
        responseDetail = responseData.description
          || responseData.message
          || responseData.error
          || (responseData.errors ? JSON.stringify(responseData.errors) : '');
      } catch (error) {
        responseDetail = responseText;
      }
    } catch (error) {
      responseDetail = '';
    }

    responseDetail = String(responseDetail || 'No additional response details were provided.')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 500);
    const requestId = response.headers.get('x-request-id');
    const requestDetail = requestId ? ` Shopify request ID: ${requestId}.` : '';
    const detailPunctuation = /[.!?]$/.test(responseDetail) ? '' : '.';
    return new Error(`HTTP ${response.status}${response.statusText ? ` ${response.statusText}` : ''}: ${responseDetail}${detailPunctuation}${requestDetail}`);
  }

  function renderedCartItemCount() {
    return Number(drawer()?.querySelector('[data-pocket-cart-render]')?.dataset.cartItemCount || 0);
  }

  function hasAcceleratedCheckoutContent(element) {
    return Boolean(element?.querySelector('shopify-accelerated-checkout-cart, .shopify-payment-button, iframe, [data-shopify-buttoncontainer]'));
  }

  function preserveAcceleratedCheckout(current, next) {
    const currentAccelerated = current?.querySelector('.pocket-cart__accelerated');
    const nextAccelerated = next?.querySelector('.pocket-cart__accelerated');

    if (hasAcceleratedCheckoutContent(currentAccelerated) && nextAccelerated) {
      nextAccelerated.replaceWith(currentAccelerated);
    }
  }

  function removeAcceleratedCheckoutLoader() {
    drawer()?.querySelector('.pocket-cart__accelerated-loader')?.remove();
  }

  function initAcceleratedCheckout(attempt = 0) {
    const accelerated = drawer()?.querySelector('.pocket-cart__accelerated');
    if (!accelerated) return;

    window.customElements?.upgrade?.(accelerated);

    if (window.Shopify?.PaymentButton?.init) {
      window.Shopify.PaymentButton.init();
    }

    accelerated.querySelectorAll('shopify-accelerated-checkout-cart, shopify-accelerated-checkout').forEach((checkout) => {
      if (!window.customElements?.whenDefined || !checkout.localName.includes('-')) return;
      window.customElements.whenDefined(checkout.localName)
        .then(() => {
          window.customElements.upgrade(accelerated);
          window.Shopify?.PaymentButton?.init?.();
        })
        .catch(() => {});
    });

    if (accelerated.querySelector('iframe, .shopify-payment-button__button, [data-shopify-buttoncontainer] button, [data-shopify-buttoncontainer] iframe')) {
      return;
    }

    if (hasAcceleratedCheckoutContent(accelerated) && attempt < 20) {
      window.setTimeout(() => initAcceleratedCheckout(attempt + 1), attempt < 5 ? 100 : 250);
    }
  }

  function formatMoney(cents) {
    const amount = (Number(cents) / 100).toFixed(2);
    const amountNoDecimals = Math.round(Number(cents) / 100).toString();
    return (window.theme?.moneyFormat || '${{amount}}')
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
      .replace(/\{\{\s*amount\s*\}\}/g, amount)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','));
  }

  function formatMoneyWithCurrency(cents, currency = 'USD') {
    return `${formatMoney(cents)} ${currency}`;
  }

  function trashIcon() {
    return '<svg class="pocket-cart__trash" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 6h18M8 6V4h8v2m-9 0 1 14h8l1-14M10 10v6m4-6v6"/></svg>';
  }

  function updateQuantityButton(button, quantity, label) {
    if (!button) return;
    button.dataset.cartQuantity = String(quantity);
    button.setAttribute('aria-label', label);
  }

  function captureCartFocus() {
    const cart = drawer();
    const active = document.activeElement;
    if (!cart || !(active instanceof Element) || !cart.contains(active)) return null;

    const line = active.closest('[data-variant-id]');
    if (line) {
      const lines = Array.from(cart.querySelectorAll('[data-variant-id]'));
      const lineIndex = lines.indexOf(line);
      return {
        type: 'line',
        variantId: line.dataset.variantId,
        action: active.closest('[data-cart-quantity-action]')?.dataset.cartQuantityAction,
        fallbackVariantIds: [lines[lineIndex + 1]?.dataset.variantId, lines[lineIndex - 1]?.dataset.variantId].filter(Boolean)
      };
    }

    const toolToggle = active.closest('[data-cart-tool-toggle]');
    if (toolToggle) return { type: 'tool', name: toolToggle.dataset.cartToolToggle };
    const toolPanel = active.closest('[data-cart-tool-panel]');
    if (toolPanel) return { type: 'tool', name: toolPanel.dataset.cartToolPanel };
    if (active.closest('[data-cart-items-toggle]')) return { type: 'items-toggle' };
    if (active.id) return { type: 'id', id: active.id };
    return { type: 'fallback' };
  }

  function restoreCartFocus(focusState) {
    if (!focusState) return;
    window.requestAnimationFrame(() => {
      const cart = drawer();
      if (!cart) return;
      let target = null;

      if (focusState.type === 'line') {
        const lines = Array.from(cart.querySelectorAll('[data-variant-id]'));
        const matchingLine = lines.find((line) => line.dataset.variantId === String(focusState.variantId));
        target = matchingLine?.querySelector(`[data-cart-quantity-action="${focusState.action}"]`)
          || matchingLine?.querySelector('[data-cart-quantity]');
        if (!target) {
          const fallbackLine = lines.find((line) => focusState.fallbackVariantIds.includes(line.dataset.variantId));
          target = fallbackLine?.querySelector('[data-cart-quantity]');
        }
      } else if (focusState.type === 'tool') {
        target = cart.querySelector(`[data-cart-tool-toggle="${focusState.name}"]`);
      } else if (focusState.type === 'items-toggle') {
        target = cart.querySelector('[data-cart-items-toggle]');
      } else if (focusState.type === 'id') {
        target = cart.querySelector(`#${CSS.escape(focusState.id)}`);
      }

      const itemsToggle = cart.querySelector('[data-cart-items-toggle]');
      if (!target || target.getClientRects().length === 0) {
        target = itemsToggle?.getClientRects().length
          ? itemsToggle
          : cart.querySelector('.pocket-cart__checkout') || cart.querySelector('.pocket-cart__close') || panel();
      }
      target?.focus({ preventScroll: true });
    });
  }

  function highlightUpdatedLine(variantId) {
    if (!variantId) return;
    const line = Array.from(drawer()?.querySelectorAll('[data-variant-id]') || [])
      .find((candidate) => candidate.dataset.variantId === String(variantId));
    if (!line) return;
    line.classList.remove('is-quantity-updated');
    void line.offsetWidth;
    line.classList.add('is-quantity-updated');
    window.setTimeout(() => line.classList.remove('is-quantity-updated'), 700);
  }

  function updateRenderedCart(cartData) {
    const render = drawer()?.querySelector('[data-pocket-cart-render]');
    if (!render) return;

    const itemsByKey = new Map(cartData.items.map((item) => [item.key, item]));
    const itemsByVariantId = new Map(cartData.items.map((item) => [String(item.variant_id), item]));
    render.dataset.cartItemCount = String(cartData.item_count);

    const title = render.querySelector('[data-pocket-cart-title]');
    if (title) title.textContent = title.textContent.replace(/\(\d+\)\s*$/, `(${cartData.item_count})`);
    const itemsCount = render.querySelector('[data-cart-items-count]');
    if (itemsCount) itemsCount.textContent = cartData.item_count;

    render.querySelectorAll('[data-line-key]').forEach((line) => {
      const item = itemsByKey.get(line.dataset.lineKey) || itemsByVariantId.get(line.dataset.variantId);
      if (!item) {
        return;
      }

      line.dataset.lineKey = item.key;
      line.dataset.variantId = String(item.variant_id);
      const quantityLabel = item.product_title || item.title;
      const isFreeItem = quantityLabel.toLowerCase().includes('free');
      line.dataset.freeItem = String(isFreeItem);
      const quantityButtons = line.querySelectorAll('[data-cart-quantity]');
      updateQuantityButton(quantityButtons[0], isFreeItem ? 0 : item.quantity - 1, `${isFreeItem || item.quantity === 1 ? 'Remove' : 'Decrease'} ${quantityLabel} quantity`);
      if (isFreeItem) {
        quantityButtons[1]?.remove();
      } else {
        updateQuantityButton(quantityButtons[1], item.quantity + 1, `Increase ${quantityLabel} quantity`);
      }
      if (quantityButtons[0]) quantityButtons[0].innerHTML = isFreeItem || item.quantity === 1 ? trashIcon() : '-';

      const quantity = line.querySelector('[data-cart-line-quantity]');
      if (quantity) quantity.textContent = item.quantity;

      const price = line.querySelector('[data-cart-line-price]');
      if (price) price.textContent = formatMoney(item.final_line_price);

      const priceWrapper = line.querySelector('.pocket-cart__item-price');
      let compare = line.querySelector('[data-cart-line-compare]');
      if (item.original_line_price > item.final_line_price) {
        if (!compare && priceWrapper) {
          compare = document.createElement('s');
          compare.dataset.cartLineCompare = '';
          priceWrapper.prepend(compare);
        }
        compare.textContent = formatMoney(item.original_line_price);
      } else {
        compare?.remove();
      }
    });

    const total = render.querySelector('[data-cart-total-price]');
    if (total) total.textContent = formatMoneyWithCurrency(cartData.total_price, cartData.currency);
    document.querySelectorAll('[data-cart-button-count]').forEach((count) => { count.textContent = cartData.item_count; });
  }

  function renderBundledCartSection(cartData, {
    allowEmpty = false,
    reopenItems = false,
    visibleVariantId,
    itemsScrollTop = 0,
    focusState
  } = {}) {
    const sectionHtml = cartData.sections?.['pocket-cart'];
    if (!sectionHtml) return false;

    const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const next = parsed.querySelector('[data-pocket-cart-render]');
    const nextItemCount = Number(next?.dataset.cartItemCount);
    if (!next || nextItemCount !== Number(cartData.item_count)) return false;

    const current = drawer()?.querySelector('[data-pocket-cart-render]');
    if (!current) return false;
    const currentItemCount = Number(current.dataset.cartItemCount || 0);
    if (!allowEmpty && currentItemCount > 0 && nextItemCount === 0) return false;

    preserveAcceleratedCheckout(current, next);
    current.replaceWith(next);
    setCartItemsOpen(reopenItems && nextItemCount > 0);
    if (reopenItems && nextItemCount > 0) {
      const items = drawer()?.querySelector('[data-cart-items-panel]');
      if (items) items.scrollTop = itemsScrollTop;
      const updatedLine = Array.from(items?.querySelectorAll('[data-variant-id]') || [])
        .find((line) => line.dataset.variantId === String(visibleVariantId));
      updatedLine?.scrollIntoView({ block: 'nearest' });
    }
    highlightUpdatedLine(visibleVariantId);
    restoreCartFocus(focusState);
    if (cartData.item_count > 0) removeAcceleratedCheckoutLoader();
    document.querySelectorAll('[data-cart-button-count]').forEach((count) => { count.textContent = cartData.item_count; });
    initMessages();
    initAcceleratedCheckout();
    loadRecommendations();
    return true;
  }

  function openCart(trigger) {
    const cart = drawer();
    if (!cart) return;
    lastTrigger = trigger || document.activeElement;
    cart.classList.add('is-open');
    document.body.classList.add('pocket-cart-open');
    if (renderedCartItemCount() > 0 && window.matchMedia('(max-width: 749px)').matches) {
      setCartItemsOpen(cartItemsPreference ?? true);
    }
    panel()?.focus();
    loadRecommendations();
    scheduleRecommendationsOpen();
    window.requestAnimationFrame(() => initAcceleratedCheckout());
    window.setTimeout(() => initAcceleratedCheckout(), 300);
  }

  function closeCart() {
    clearTimeout(recommendationsOpenTimer);
    setRecommendationsOpen(false);
    drawer()?.classList.remove('is-open');
    document.body.classList.remove('pocket-cart-open');
    lastTrigger?.focus?.();
  }

  async function refreshCart({ open = true, expectedItemCount, updateVersion } = {}) {
    const activeUpdateVersion = updateVersion || beginCartUpdate();
    try {
      const currentItemCount = renderedCartItemCount();
      const reopenItems = drawer()?.classList.contains('is-cart-items-open') || false;
      const focusState = captureCartFocus();
      const allowEmpty = expectedItemCount === 0;
      let cartData = null;
      let itemCount = null;

      for (let attempt = 0; attempt < 5; attempt += 1) {
        const response = await fetch(`${root()}cart.js?_=${Date.now()}`, {
          cache: 'no-store',
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error('Unable to read cart');
        cartData = await response.json();

        const reachedExpectedCount = expectedItemCount === undefined
          || expectedItemCount === 0
          || cartData.item_count >= expectedItemCount;
        if (reachedExpectedCount && (cartData.item_count > 0 || currentItemCount === 0 || allowEmpty)) {
          itemCount = cartData.item_count;
          break;
        }
        await new Promise((resolve) => setTimeout(resolve, 150));
      }

      if (itemCount === null) return;
      if (!allowEmpty && currentItemCount > 0 && itemCount === 0) return;

      let next = null;

      for (let attempt = 0; attempt < 8; attempt += 1) {
        const response = await fetch(`${root()}?sections=pocket-cart&_=${Date.now()}`, {
          cache: 'no-store',
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error('Unable to refresh cart');
        const sections = await response.json();
        const parsed = new DOMParser().parseFromString(sections['pocket-cart'], 'text/html');
        const candidate = parsed.querySelector('[data-pocket-cart-render]');
        const renderedItemCount = Number(candidate?.dataset.cartItemCount || 0);

        if (renderedItemCount === itemCount && (allowEmpty || currentItemCount === 0 || renderedItemCount > 0)) {
          next = candidate;
          break;
        }

        await new Promise((resolve) => setTimeout(resolve, 200));
      }

      if (activeUpdateVersion !== cartUpdateVersion) return;
      if (!next) return;
      const current = drawer()?.querySelector('[data-pocket-cart-render]');
      preserveAcceleratedCheckout(current, next);
      if (next && current) current.replaceWith(next);
      setCartItemsOpen(reopenItems && itemCount > 0);
      if (itemCount > 0) removeAcceleratedCheckoutLoader();
      document.querySelectorAll('[data-cart-button-count]').forEach((count) => { count.textContent = itemCount; });
      if (open) openCart();
      initMessages();
      initAcceleratedCheckout();
      restoreCartFocus(focusState);
    } finally {
      if (activeUpdateVersion === cartUpdateVersion) setUpdating(false);
    }
  }

  async function changeLine(key, quantity) {
    const cart = drawer();
    if (cart?.classList.contains('is-updating') || cart?.classList.contains('is-removing')) return;
    const requestedQuantity = Math.max(0, Number(quantity));
    const cartLines = Array.from(cart?.querySelectorAll('[data-line-key]') || []);
    const currentLine = cartLines.find((line) => line.dataset.lineKey === key);
    const currentLineQuantity = Number(currentLine?.querySelector('[data-cart-line-quantity]')?.textContent || 0);
    const visibleVariantId = currentLine?.dataset.variantId;
    const itemsScrollTop = cart?.querySelector('[data-cart-items-panel]')?.scrollTop || 0;
    // Free/gift-with-purchase lines are automatically removed by Shopify once the
    // qualifying paid items are gone, so the cart legitimately empties out in that
    // case. Base "allowEmpty" on the remaining *paid* item quantity only, otherwise
    // a valid gift-cascade removal gets misreported as an unexpected empty-cart error.
    const otherPaidQuantity = cartLines
      .filter((line) => line !== currentLine && line.dataset.freeItem !== 'true')
      .reduce((sum, line) => sum + Number(line.querySelector('[data-cart-line-quantity]')?.textContent || 0), 0);
    const currentLineIsFree = currentLine?.dataset.freeItem === 'true';
    const expectedPaidItemCount = currentLineIsFree ? otherPaidQuantity : otherPaidQuantity + requestedQuantity;
    const allowEmpty = expectedPaidItemCount === 0;
    const reopenItems = cart?.classList.contains('is-cart-items-open') || false;
    const focusState = captureCartFocus();
    if (requestedQuantity === 0 && currentLine) {
      cart.classList.add('is-removing');
      currentLine.classList.add('is-removing');
      await new Promise((resolve) => setTimeout(resolve, 280));
      cart.classList.remove('is-removing');
    }
    const updateVersion = beginCartUpdate();
    try {
      const response = await fetch(`${root()}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity: requestedQuantity,
          sections: ['pocket-cart'],
          sections_url: window.location.pathname
        })
      });
      if (!response.ok) throw await createCartResponseError(response);
      let updatedCart;
      try {
        updatedCart = await response.json();
      } catch (error) {
        throw new Error('Shopify returned an unreadable cart response.');
      }
      if (updateVersion !== cartUpdateVersion) return;

      if (!allowEmpty && updatedCart.item_count === 0) {
        for (let attempt = 0; attempt < 5; attempt += 1) {
          await new Promise((resolve) => setTimeout(resolve, attempt === 0 ? 100 : 200));
          const verification = await fetch(`${root()}cart.js?_=${Date.now()}`, {
            cache: 'no-store',
            headers: { Accept: 'application/json' }
          });
          if (!verification.ok) continue;
          const verifiedCart = await verification.json();
          if (verifiedCart.item_count > 0) {
            updatedCart = verifiedCart;
            break;
          }
        }
        if (updatedCart.item_count === 0) {
          throw new Error('Cart update returned an unexpected empty cart');
        }
      }

      if (!renderBundledCartSection(updatedCart, {
        allowEmpty,
        reopenItems,
        visibleVariantId,
        itemsScrollTop,
        focusState
      }) && updatedCart.item_count > 0) {
        updateRenderedCart(updatedCart);
        setCartItemsOpen(reopenItems);
        highlightUpdatedLine(visibleVariantId);
        restoreCartFocus(focusState);
      }
    } catch (error) {
      currentLine?.classList.remove('is-removing');
      const itemTitle = currentLine?.querySelector('.pocket-cart__item-title')?.textContent.trim() || 'Unknown item';
      const diagnostics = [
        `Item: ${itemTitle}`,
        `Requested quantity: ${requestedQuantity}`,
        `Details: ${error.message || 'Unknown cart update error'}`,
        `Time: ${new Date().toISOString()}`,
        `Browser online: ${navigator.onLine ? 'yes' : 'no'}`
      ].join(' | ');
      showCartToast(
        `A cart update error has occurred. ${diagnostics}. Please send this entire error message to support@ruleoneproteins.com. Then refresh the page and try again.`,
        'error'
      );
      throw error;
    } finally {
      cart?.classList.remove('is-removing');
      if (updateVersion === cartUpdateVersion) setUpdating(false);
    }
  }

  async function addVariant(id, button) {
    const updateVersion = beginCartUpdate();
    const expectedItemCount = renderedCartItemCount() + 1;
    button.disabled = true;
    try {
      const response = await fetch(`${root()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
      });
      if (!response.ok) throw new Error('Unable to add recommendation');
      await refreshCart({ expectedItemCount, updateVersion });
    } finally {
      if (updateVersion === cartUpdateVersion) setUpdating(false);
      button.disabled = false;
    }
  }

  function activeDiscountCodes() {
    return Array.from(drawer()?.querySelectorAll('[data-remove-discount]') || [])
      .map((button) => button.dataset.removeDiscount)
      .filter(Boolean);
  }

  async function updateDiscountCodes(codes) {
    const response = await fetch(`${root()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: codes.join(',') })
    });
    if (!response.ok) throw await createCartResponseError(response);
    return response.json();
  }

  async function applyDiscount(form) {
    const code = new FormData(form).get('discount')?.trim();
    if (!code) return;
    const existingCodes = activeDiscountCodes();
    const requestedCodes = existingCodes.some((existingCode) => existingCode.toLowerCase() === code.toLowerCase())
      ? existingCodes
      : [...existingCodes, code];
    const updateVersion = beginCartUpdate();
    const message = form.querySelector('[data-discount-message]');
    message.textContent = 'Applying…';
    try {
      const updatedCart = await updateDiscountCodes(requestedCodes);
      const submittedCode = updatedCart.discount_codes?.find((discount) => discount.code.toLowerCase() === code.toLowerCase());
      if (!submittedCode || submittedCode.applicable === false) {
        await updateDiscountCodes(existingCodes);
        message.textContent = 'That code is not valid for the current cart.';
        return;
      }
      await refreshCart({ expectedItemCount: updatedCart.item_count, updateVersion });
      showCartToast(`Discount code ${submittedCode.code} applied.`);
    } catch (error) {
      message.textContent = 'That code could not be applied.';
      showCartToast(error.message || message.textContent, 'error');
    } finally {
      if (updateVersion === cartUpdateVersion) setUpdating(false);
    }
  }

  async function removeDiscount(code, button) {
    if (!code) return;
    const remainingCodes = activeDiscountCodes()
      .filter((activeCode) => activeCode.toLowerCase() !== code.toLowerCase());
    const updateVersion = beginCartUpdate();
    button.disabled = true;
    try {
      const updatedCart = await updateDiscountCodes(remainingCodes);
      await refreshCart({ expectedItemCount: updatedCart.item_count, updateVersion });
      showCartToast(`Discount code ${code} removed.`);
    } catch (error) {
      button.disabled = false;
      showCartToast(error.message || 'The discount code could not be removed.', 'error');
    } finally {
      if (updateVersion === cartUpdateVersion) setUpdating(false);
    }
  }

  function toggleCartTool(name) {
    const cart = drawer();
    if (!cart) return;
    const selectedButton = cart.querySelector(`[data-cart-tool-toggle="${name}"]`);
    const shouldOpen = selectedButton?.getAttribute('aria-expanded') !== 'true';

    cart.querySelectorAll('[data-cart-tool-toggle]').forEach((button) => {
      button.setAttribute('aria-expanded', String(shouldOpen && button.dataset.cartToolToggle === name));
    });
    cart.querySelectorAll('[data-cart-tool-panel]').forEach((toolPanel) => {
      toolPanel.hidden = !(shouldOpen && toolPanel.dataset.cartToolPanel === name);
    });
    // The tool panel floats above the tools bar and can overlap the recommendations
    // section on narrow layouts, so hide recommendations while a panel is open.
    cart.classList.toggle('is-cart-tool-open', shouldOpen);
    if (shouldOpen) setCartItemsOpen(false);

  }

  function setCartItemsOpen(isOpen) {
    const cart = drawer();
    const button = cart?.querySelector('[data-cart-items-toggle]');
    cart?.classList.toggle('is-cart-items-open', isOpen);
    if (button) button.setAttribute('aria-expanded', String(isOpen));
    const action = button?.querySelector('[data-cart-items-action]');
    if (action) action.textContent = isOpen ? 'Hide items' : 'View items';
  }

  function toggleCartItems() {
    const cart = drawer();
    const button = cart?.querySelector('[data-cart-items-toggle]');
    if (!cart || !button) return;
    const shouldOpen = button.getAttribute('aria-expanded') !== 'true';
    clearTimeout(recommendationsOpenTimer);
    cartItemsPreference = shouldOpen;
    setCartItemsOpen(shouldOpen);
    if (shouldOpen) {
      cart.querySelectorAll('[data-cart-tool-toggle]').forEach((toolButton) => {
        toolButton.setAttribute('aria-expanded', 'false');
      });
      cart.querySelectorAll('[data-cart-tool-panel]').forEach((toolPanel) => {
        toolPanel.hidden = true;
      });
    }
  }

  async function copyText(textToCopy) {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(textToCopy);
      return;
    }
    const input = document.createElement('textarea');
    input.value = textToCopy;
    input.style.position = 'fixed';
    input.style.opacity = '0';
    document.body.append(input);
    input.select();
    document.execCommand('copy');
    input.remove();
  }

  async function shareCart(button) {
    const message = button.parentElement.querySelector('[data-share-message]');
    button.disabled = true;
    message.textContent = '';
    try {
      const quantities = new Map();
      drawer()?.querySelectorAll('[data-variant-id]').forEach((line) => {
        const variantId = line.dataset.variantId;
        const quantity = Number(line.querySelector('[data-cart-line-quantity]')?.textContent || 0);
        quantities.set(variantId, (quantities.get(variantId) || 0) + quantity);
      });
      const lines = Array.from(quantities, ([variantId, quantity]) => `${variantId}:${quantity}`);
      if (!lines.length) throw new Error('Add an item before sharing your cart.');
      const shareUrl = `${window.location.origin}${root()}cart/${lines.join(',')}`;

      if (navigator.share) {
        try {
          await navigator.share({ title: 'Rule 1 Proteins Cart', url: shareUrl });
          message.textContent = 'Cart shared.';
          showCartToast('Cart shared.');
          return;
        } catch (error) {
          if (error.name === 'AbortError') {
            message.textContent = '';
            return;
          }
        }
      }

      await copyText(shareUrl);
      message.textContent = 'Cart link copied.';
      showCartToast('Cart link copied.');
    } catch (error) {
      message.textContent = error.message || 'Unable to share this cart.';
      showCartToast(message.textContent, 'error');
    } finally {
      button.disabled = false;
    }
  }

  async function loadRecommendations() {
    const target = drawer()?.querySelector('[data-pocket-cart-recommendations]');
    if (!target || target.dataset.loaded || target.dataset.loading || !target.dataset.productId) return;
    const content = target.querySelector('[data-pocket-cart-recommendations-content]');
    if (!content) return;
    target.dataset.loading = 'true';
    target.hidden = false;
    try {
      const recommendationBase = target.dataset.recommendationsUrl || `${root()}recommendations/products`;
      const recommendationUrl = (intent) => {
        const url = new URL(recommendationBase, window.location.origin);
        url.searchParams.set('section_id', 'pocket-cart-recommendations');
        url.searchParams.set('product_id', target.dataset.productId);
        url.searchParams.set('limit', target.dataset.limit || 5);
        url.searchParams.set('intent', intent);
        return url.toString();
      };
      const fetchRecommendations = async (intent) => {
        const response = await fetch(recommendationUrl(intent), { cache: 'no-store' });
        if (!response.ok) throw new Error('Unable to load recommendations');
        const text = await response.text();
        return new DOMParser().parseFromString(text, 'text/html').querySelector('.pocket-cart-recs');
      };
      let recommendations = await fetchRecommendations('complementary');
      if (!recommendations) recommendations = await fetchRecommendations('related');
      content.innerHTML = recommendations ? recommendations.outerHTML : '';
      target.dataset.loaded = 'true';
      target.dataset.hasRecommendations = String(Boolean(recommendations));
      content.hidden = !recommendations;
      if (!recommendations) target.hidden = true;
      if (recommendations && drawer()?.classList.contains('is-open')) scheduleRecommendationsOpen();
    } catch (error) {
      delete target.dataset.loaded;
      target.hidden = false;
    } finally {
      delete target.dataset.loading;
    }
  }

  function showMessage(index) {
    const cart = drawer();
    if (!cart) return;
    activeMessageIndex = index;
    cart.querySelectorAll('[data-message-slide]').forEach((slide, i) => slide.classList.toggle('is-active', i === index));
    cart.querySelectorAll('[data-message-dot]').forEach((dot, i) => dot.classList.toggle('is-active', i === index));
  }

  function initMessages() {
    clearInterval(messageTimer);
    const slides = drawer()?.querySelectorAll('[data-message-slide]') || [];
    if (slides.length < 2) return;
    // Preserve whichever slide is currently selected (e.g. via a manual dot click)
    // instead of always restarting from 0, so re-renders don't undo the user's choice.
    if (activeMessageIndex >= slides.length) activeMessageIndex = 0;
    messageTimer = setInterval(() => {
      activeMessageIndex = (activeMessageIndex + 1) % slides.length;
      showMessage(activeMessageIndex);
    }, 5000);
  }

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-pocket-cart-open], [data-cart-button]');
    if (opener) { event.preventDefault(); openCart(opener); return; }
    if (event.target.closest('[data-pocket-cart-close]')) { closeCart(); return; }
    const quantity = event.target.closest('[data-cart-quantity]');
    if (quantity) { changeLine(quantity.closest('[data-line-key]').dataset.lineKey, quantity.dataset.cartQuantity).catch(console.error); return; }
    const remove = event.target.closest('[data-cart-remove]');
    if (remove) { changeLine(remove.closest('[data-line-key]').dataset.lineKey, 0).catch(console.error); return; }
    const dot = event.target.closest('[data-message-dot]');
    if (dot) { showMessage(Number(dot.dataset.messageDot)); return; }
    if (event.target.closest('[data-cart-items-toggle]')) { toggleCartItems(); return; }
    const toolToggle = event.target.closest('[data-cart-tool-toggle]');
    if (toolToggle) { toggleCartTool(toolToggle.dataset.cartToolToggle); return; }
    const recommendationsToggle = event.target.closest('[data-recommendations-toggle]');
    if (recommendationsToggle) {
      if (!window.matchMedia('(max-width: 749px)').matches) return;
      clearTimeout(recommendationsOpenTimer);
      const accordion = recommendationsToggle.closest('[data-recommendations-accordion]');
      setRecommendationsOpen(!accordion.classList.contains('is-open'));
      return;
    }
    const removeDiscountButton = event.target.closest('[data-remove-discount]');
    if (removeDiscountButton) {
      removeDiscount(removeDiscountButton.dataset.removeDiscount, removeDiscountButton).catch(console.error);
      return;
    }
    const shareButton = event.target.closest('[data-share-cart]');
    if (shareButton) { shareCart(shareButton).catch(console.error); return; }
    const add = event.target.closest('[data-recommendation-add]');
    if (add) {
      const variant = add.closest('.pocket-cart-rec').querySelector('[data-recommendation-variant]');
      addVariant(variant.value, add).catch(console.error);
    }
  });

  document.addEventListener('submit', (event) => {
    if (event.target.matches('[data-discount-form]')) {
      event.preventDefault();
      applyDiscount(event.target).catch(console.error);
      return;
    }
  });

  document.addEventListener('change', (event) => {
    if (!event.target.matches('select[data-recommendation-variant]')) return;
    const card = event.target.closest('.pocket-cart-rec');
    const selectedOption = event.target.selectedOptions[0];
    const addButton = card.querySelector('[data-recommendation-add]');
    addButton.disabled = !event.target.value || selectedOption?.disabled;
    if (selectedOption?.dataset.price) {
      card.querySelector('[data-recommendation-price]').textContent = formatMoney(selectedOption.dataset.price);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && drawer()?.classList.contains('is-open')) closeCart();
  });

  document.addEventListener('pocket-cart:refresh', (event) => refreshCart(event.detail).catch(console.error));
  document.addEventListener('DOMContentLoaded', () => {
    initMessages();
    initAcceleratedCheckout();
    const currentPath = window.location.pathname.replace(/\/+$/, '') || '/';
    const cartPath = `${root()}cart`.replace(/\/{2,}/g, '/').replace(/\/+$/, '');
    const returnedToCartPage = currentPath === cartPath;
    if (document.cookie.includes('cart_updated=1')) {
      document.cookie = 'cart_updated=; Max-Age=0; path=/';
      openCart();
    } else if (returnedToCartPage) {
      openCart();
    }
  });
})();
